import React, { useMemo, useState } from 'react';
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import { BONUS_SOURCES } from "../../../../config/bonusSources";
import styles from './bonusBreakdown.module.scss';

const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.48)',
        zIndex: 1000,
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: 'none',
        padding: 0,
        borderRadius: 24,
        maxWidth: 760,
        width: 'calc(100vw - 48px)',
    },
};

function formatExpiration(timestamp) {
    if (!timestamp || timestamp === 0) return '-';
    const d = new Date(timestamp);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function getSourceData(userBalance, sourceKey) {
    if (!userBalance || !userBalance[sourceKey]) {
        return { bonuses: [], total: 0, validTotal: 0, nearestExpiry: 0, allExpired: false };
    }

    const bonuses = userBalance[sourceKey].bonuses || userBalance[sourceKey] || [];
    if (!Array.isArray(bonuses)) return { bonuses: [], total: 0, validTotal: 0, nearestExpiry: 0, allExpired: false };

    const now = Date.now();
    let total = 0;
    let validTotal = 0;
    let nearestExpiry = 0;
    let hasAny = false;

    for (const b of bonuses) {
        const amount = b.amount || 0;
        total += amount;
        const isValid = b.expired_at === 0 || b.expired_at > now;

        if (isValid) {
            validTotal += amount;
            if (b.expired_at > 0 && (nearestExpiry === 0 || b.expired_at < nearestExpiry)) {
                nearestExpiry = b.expired_at;
            }
        }

        hasAny = true;
    }

    return {
        bonuses,
        total,
        validTotal,
        nearestExpiry,
        allExpired: hasAny && validTotal === 0,
    };
}

export default function BonusBreakdown({ userBalance }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const sourceDataList = useMemo(() => {
        if (!userBalance) return [];

        return BONUS_SOURCES.map((source) => ({
            ...source,
            data: getSourceData(userBalance, source.key),
        })).filter(({ data }) => data.total > 0);
    }, [userBalance]);

    const cumulativeTotal = sourceDataList.reduce((sum, s) => sum + s.data.validTotal, 0);

    if (!sourceDataList.length) return null;

    return (
        <>
            <button
                type="button"
                className={styles.trigger}
                onClick={() => setIsOpen(true)}
            >
                <span className={styles.triggerIcon} aria-hidden="true">
                    <span />
                </span>
                <span className={styles.triggerCopy}>
                    <span className={styles.triggerLabel}>{t('NSBonusMode.showBreakdown')}</span>
                    <span className={styles.triggerHint}>{t('NSBonusMode.cascadeBadgeTooltip')}</span>
                </span>
            </button>

            <Modal
                ariaHideApp={false}
                isOpen={isOpen}
                onRequestClose={() => setIsOpen(false)}
                style={modalStyles}
            >
                <div className={styles.modal}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        aria-label={t('close')}
                        onClick={() => setIsOpen(false)}
                    >
                        x
                    </button>

                    <div className={styles.modalHeader}>
                        <div className={styles.modalBadge} aria-hidden="true">
                            <span />
                        </div>
                        <div>
                            <h3>{t('NSBonusMode.showBreakdown')}</h3>
                            <p>{t('NSBonusMode.cascadeBadgeTooltip')}</p>
                        </div>
                    </div>

                    <div className={styles.totalCard}>
                        <span>{t('bonuses')}</span>
                        <strong>{Math.floor(cumulativeTotal)}</strong>
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('NSBonusMode.source')}</th>
                                    <th>{t('bonuses')}</th>
                                    <th>{t('NSBonusMode.expiration')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sourceDataList.map(({ key, labelKey, data }) => {
                                    const isExpired = data.allExpired && data.total > 0;

                                    return (
                                        <tr key={key} className={isExpired ? styles.expiredRow : ''}>
                                            <td>{t(labelKey)}</td>
                                            <td className={styles.amountCell}>
                                                {isExpired ? (
                                                    <>
                                                        <span>{Math.floor(data.total)}</span>
                                                        <span className={styles.expiredLabel}>{t('NSBonusMode.expired')}</span>
                                                    </>
                                                ) : (
                                                    <span className={data.validTotal === 0 ? styles.zeroAmount : ''}>
                                                        {data.validTotal > 0 ? Math.floor(data.validTotal) : '-'}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={styles.expirationChip}>
                                                    {data.nearestExpiry > 0
                                                        ? formatExpiration(data.nearestExpiry)
                                                        : (data.validTotal > 0 ? t('NSBonusMode.noExpiration') : '-')
                                                    }
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>
        </>
    );
}
